import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import VoiceChatListRow from "../../../components/VoiceChatListRow/VoiceChatListRow";
import RecordAudioModal from "../../../components/RecordAudioModal/RecordAudioModal";
import RecordIcon from "../../../assets/icons/audio-record-light-bg.svg";
import { AppDispatch } from "../../../redux/store";
import { getQuickFileAlbumDetails } from "../../../redux/quickFile/quickFileSlice";
import "./VoiceChatList.less";
import HeaderTitlePage from "../../../components/HeaderTitlePage";
import Loader from "../../../components/loader/Loader";
import { message } from "antd";
import { storageService } from "../../../services";
import { quickFileService } from "../../../services/quickFile.service";

interface LocationState {
  albumName?: string;
}

const VoiceChatList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState };
  const { albumId: albumIdFromRoute } = useParams();
  const albumId = albumIdFromRoute;
  const albumName = location?.state?.albumName || t("new-album") || "Audio Album";
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [loadedData, setLoadedData] = useState(0);
  const [progress, setProgress] = useState(0);
  const quickFileItem = useSelector((state: any) => state.quickFile.item);
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();

  useEffect(() => {
    if (albumId) {
      setIsLoading(true);
      const action = dispatch(getQuickFileAlbumDetails(albumId));
      // Support both promise and non-promise dispatch results
      Promise.resolve(action).finally(() => setIsLoading(false));
    }
  }, [albumId, dispatch]);

  const handleUploadSuccess = () => {
    // Refresh the album details after successful upload
    if (albumId) {
      dispatch(getQuickFileAlbumDetails(albumId));
    }
    // Close the modal
    setIsRecordOpen(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input for subsequent selections
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!albumId) {
      message.error(t("no-album-selected") || "No album selected");
      return;
    }

    if (!file.type.includes("audio/")) {
      message.error(t("please-select-audio-file") || "Please select an audio file");
      return;
    }

    // Load duration before upload
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    audio.src = url;
    audio.onloadedmetadata = async () => {
      const durationSeconds = Math.max(0, Math.floor(audio.duration || 0));
      URL.revokeObjectURL(url);

      try {
        setIsUploading(true);
        setLoadedData(0);
        setProgress(0);
        const hide = message.loading(t("audio-uploading") || "Audio uploading", 0);

        const audioFile = {
          name: file.name,
          type: file.type,
          size: file.size,
          originFileObj: file,
          time: durationSeconds,
        } as any;

        const result: any = await storageService.uploadFileMultiPart(audioFile, setLoadedData, file.size, "quickFiles");

        const audioFiles: any[] = [
          {
            Location: result.Location,
            isPrivate: false,
            Key: result.Key,
            ETag: result.ETag,
            Bucket: result.Bucket,
            filename: audioFile.name,
            size: audioFile.size,
            title: audioFile.name,
            time: durationSeconds,
          },
        ];

        const creatorId = localStorage.getItem("selectedCreator");
        const formData = new FormData();
        formData.append("audioFiles", JSON.stringify(audioFiles));
        if (creatorId) formData.append("creatorId", creatorId);

        await quickFileService.addMediaToAlbum(albumId, formData, setProgress);

        hide();
        message.success(t("audio-upload-success") || "Audio uploaded successfully");
        setIsUploading(false);
        handleUploadSuccess();
      } catch (e) {
        console.error(e);
        message.error(t("audio-upload-failed") || "Failed to upload audio");
        setIsUploading(false);
      }
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      message.error(t("failed-to-load-audio") || "Failed to load audio file");
    };
  };

  return (
    <div className="vc-page">
      <div className="vc-header">
        <HeaderTitlePage title={albumName} fontSize="24px" navigateTo={() => navigate(-1)} />
      </div>
      <main className="vc-content">
        <div className="vc-list">
          {isLoading && <Loader />}
          {!isLoading &&
            quickFileItem?.files?.length > 0 &&
            quickFileItem.files.map((item: any) => (
              <div className="vc-list-item" key={item.fileId || item.id}>
                <VoiceChatListRow
                  file={item}
                  title={item.title || item.name || "Untitled Audio"}
                  date={formatDate(item.createdAt)}
                  duration={item.time || item.duration || 0}
                  albumId={albumId}
                  onClick={() => {}}
                  onDelete={() => {
                    if (albumId) {
                      dispatch(getQuickFileAlbumDetails(albumId));
                    }
                  }}
                />
              </div>
            ))}
          {!isLoading && (!quickFileItem?.files || quickFileItem.files.length === 0) && (
            <div className="empty-state">
              <p>{t("no-files-audio")}</p>
            </div>
          )}
        </div>
        <button className="vc-record-fab" onClick={() => setIsRecordOpen(true)} aria-label="Record audio">
          <img src={RecordIcon} alt="record" />
        </button>
        <button className="vc-upload-fab" onClick={handleUploadClick} disabled={isUploading} aria-label={String(t("import-audio-file"))}>
          {t("import-audio-file")}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.aac,.wav,.caf,.aif,.aiff,.flac,.ogg,.m4b,.webm"
          style={{ display: "none" }}
          onChange={handleFileSelected}
        />
        <RecordAudioModal isOpen={isRecordOpen} onClose={() => setIsRecordOpen(false)} onUploadSuccess={handleUploadSuccess} />
      </main>
    </div>
  );
};

export default VoiceChatList;
